<template>
  <div class="irregularity-details-info">
    <div class="irregularity-details-info__fields">
      <fields-renderer
        :config="fieldsRendererConfig"
        :form-data="formData"
        :fields-config="fieldsConfig"
        :validation-errors="validationErrors"
        :initial-autocompletes="initialAutocompletes"
        :permissions="permissions"
        :edit="edit"
        :create-mode="createMode"
        :update-data="updateData"
        :update-validation-errors="updateValidationErrors"
        :remove-data-property-by-key="removeDataPropertyByKey"
        :replace-property-value-by-key="replacePropertyValueByKey"
        @unlock-fields-on-dbl-click="unlockFieldsOnDblClick"
      />
    </div>

    <div class="irregularity-details-info__side">
      <div class="irregularity-details-info__side-inner">
        <div class="irregularity-details-info__images">
          <device-images-carousel
            :images="deviceImages"
            :title="formData && formData.irregularity_type"
          />
        </div>

        <!--      The same panel the listing uses, handed the one report - so the
                  details map draws the vehicle's route as well as the marker.-->
        <div class="irregularity-details-info__map">
          <irregularities-map-panel
            :config="mapPanelConfig"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import FieldsRenderer from '@/global/components/form-layout/FieldsRenderer.vue'
import DeviceImagesCarousel from './DeviceImagesCarousel.vue'
import IrregularitiesMapPanel from './IrregularitiesMapPanel.vue'
import { isArray } from '@/global/utils/utils'
import { IRREGULARITY_PHOTOS_KEY } from '@/modules/road-maintenance-module/winter-service-v2/irregularities/config/constants'

const props = defineProps({
  // The block's own config from the tab's `tabContent` - its single `rows` entry
  // holds the field list (see useIrregularityDetails for why it lives there).
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  fieldsConfig: {
    type: Object,
    default: () => ({})
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  initialAutocompletes: {
    type: Object,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: null
  },
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  },
  removeDataPropertyByKey: {
    type: Function,
    default: () => {}
  },
  replacePropertyValueByKey: {
    type: Function,
    default: () => {}
  }
})

const emit = defineEmits(['set-edit'])

// FieldsRenderer only reports the double-click; turning the tab into edit mode is
// the block's job, and Content.vue is what listens for `set-edit`. FieldsRenderer
// has already checked the field itself - what is left is the tab-level guard.
const unlockFieldsOnDblClick = () => {
  if (props.createMode || (props.permissions && !props.permissions.canEdit)) {
    return
  }

  emit('set-edit', true)
}

const deviceImages = computed(() => {
  const photos = props.formData?.[IRREGULARITY_PHOTOS_KEY]
  return isArray(photos) ? photos : []
})

// The panel draws the pin and the route of whatever it is handed. On the listing
// that is the marked rows; here it is the one report being looked at.
const mapPanelConfig = computed(() => ({
  irregularities: props.formData?.id ? [props.formData] : []
}))

// One field per row, so the form reads as the single column the design shows next
// to the photos and the map.
const fieldsRendererConfig = computed(() => {
  const [fieldsRow] = props.config?.rows || []

  return {
    columnsCount: fieldsRow?.columnsCount || 1,
    fields: fieldsRow?.fields || []
  }
})
</script>

<style scoped>
.irregularity-details-info {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 15px;
  /* The photos and the map keep a height of their own, so the row must not stretch
     their cell to the form's - see the side column below. */
  align-items: start;
}

.irregularity-details-info__fields {
  min-width: 0;
  padding: 16px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  box-sizing: border-box;
}

/* A height of its own rather than however tall the form beside it happens to be.
   Following the form read well enough while it was short, but a report carries a
   comment as well as its fields, and a pair of panels stretched to that is a photograph
   the size of a poster above a map that runs off the bottom of the screen. */
.irregularity-details-info__side {
  min-width: 0;
  height: 556px;
}

.irregularity-details-info__side-inner {
  height: 100%;
  display: grid;
  grid-template-rows: 220px minmax(0, 1fr);
  gap: 16px;
}

.irregularity-details-info__images {
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.irregularity-details-info__map {
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

/* The panel keeps a floor of its own so it survives the listing's side column, which
   gives it no height. Here it is handed one, and that floor would push the pair past
   the height set above. */
.irregularity-details-info__map ::v-deep .irregularities-map-panel {
  min-height: 0;
}

/* Stacked, with the full width to themselves. */
@media (max-width: 960px) {
  .irregularity-details-info {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
